import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MoodService } from '../../services/mood.service';
import { PostService } from '../../services/post.service';
import { UserSessionService } from '../../services/user-session.service';
import { DocumentService } from '../../services/document.service';
import { UserService } from '../../services/user.service';
import { ControlTask, ControltaskService } from '../../services/controltask.service';
import { ControlprojectService } from '../../services/controlproject.service';
import { ControlhelpService } from '../../services/controlhelp.service';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { API_BASE_URL } from '../../services/api-base';
import { TranslateService } from '@ngx-translate/core';
import { ControlQuestionService } from '../../services/controlquestion.service';
import { Router } from '@angular/router';

export interface DocumentActionItem {
  type: 'READ' | 'ACCEPT' | 'AGREE' | 'SIGN';
  label: string;
  completed: boolean;
}

@Component({
  selector: 'app-feed',
  templateUrl: './feed.component.html',
  styleUrls: ['./feed.component.scss']
})
export class FeedComponent implements OnInit, OnDestroy {
  @ViewChild('selfieVideo') selfieVideoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('selfieCanvas') selfieCanvasRef?: ElementRef<HTMLCanvasElement>;

  userName = '';
  currentUser: any = null;
  avatarUrl: string | null = null;
  monthBirthdays: Array<{
    id: number;
    name: string;
    birthDate: Date;
    departmentLabel: string;
    pictureUrl: string | null;
  }> = [];
  birthdaysLoading = false;
  birthdaysError = '';
  currentMonthLabel = '';
  posts: Array<any & {
    liked?: boolean;
    likesCount?: number;
    commentsCount?: number;
    commentsVisible?: boolean;
    comments?: any[];
    commentsOffset?: number;
    commentsLoading?: boolean;
    commentsEnd?: boolean;
    commentsLoaded?: boolean;
    commentsError?: string;
    newComment?: string;
    submittingComment?: boolean;
  }> = [];
  loadingPosts = false;
  postsError = '';
  previewImage: string | null = null;
  private readonly apiBase = `${API_BASE_URL}`;
  private readonly commentsPageSize = 3;
  commentEmojis = ['😀', '🙂', '😎', '🙌', '🎉', '❤️'];
  moodOptions = [
    { id: 'excellent', labelKey: 'INTRANET.MOOD.EXCELLENT', emoji: '😄' },
    { id: 'good', labelKey: 'INTRANET.MOOD.GOOD', emoji: '🙂' },
    { id: 'neutral', labelKey: 'INTRANET.MOOD.NEUTRAL', emoji: '😐' },
    { id: 'sad', labelKey: 'INTRANET.MOOD.SAD', emoji: '☹️' },
    { id: 'terrible', labelKey: 'INTRANET.MOOD.TERRIBLE', emoji: '😢' }
  ];
  moodFeedback = '';
  savingMood = false;
  selectedMood: string | null = null;
  moodSummaryRows: Array<{ id: string; emoji: string; count: number; percent: number }> = this.moodOptions.map((item) => ({
    id: item.id,
    emoji: item.emoji,
    count: 0,
    percent: 0
  }));
  moodSummaryTotal = 0;
  moodSummaryLoading = false;
  private userSub: Subscription | null = null;
  private lastUserId: number | null = null;
  popupVisible = false;
  activePopup: any = null;
  visiblePosts: typeof this.posts = [];
  visibleTimelineItems: any[] = [];
  expandedPostKeys = new Set<string>();
  private visibleCount = 0;
  private readonly postsPerPage = 3;
  private readonly postPreviewCharLimit = 220;
  pendingDocuments: any[] = [];
  publicDocuments: any[] = [];
  pendingQuestionnaires: any[] = [];
  loadingPendingDocs = false;
  loadingPublicDocs = false;
  loadingPendingQuestionnaires = false;
  latestCommunicationTitle = 'Nenhum comunicado recente';
  todayTasks: ControlTask[] = [];
  activeProjectsList: Array<{ id: number; code?: string | null; name: string; status: string; created_at: string }> = [];
  openDayMetric: 'TASKS' | 'PENDING' | 'PROJECTS' | null = null;
  overdueTask: ControlTask | null = null;
  loadingOverdueTask = false;
  myRequestsOpenCount = 0;
  loadingMyRequests = false;
  activeProjectsCount = 0;
  adminCardsOpen = {
    quickLinks: false,
    certificates: false,
    requests: false
  };
  private hasModuleList = false;
  private allowedModules = new Set<string>();
  pendingModalOpen = false;
  pendingModalTab: 'pending' | 'public' = 'pending';
  selectedTask: any = null;
  selectedTaskMode: 'pending' | 'public' = 'pending';
  uploadingAvatar = false;
  documentActionsChecklist: DocumentActionItem[] = [];
  documentScrollReady = false;
  documentModalOpen = false;
  safeDocumentUrl: SafeResourceUrl | null = null;
  documentFeedback = '';
  locationEvidence: {
    latitude: number;
    longitude: number;
    accuracy?: number | null;
    provider: string;
    source: string;
  } | null = null;
  locationLoading = false;
  locationError = '';
  selfieFile: File | null = null;
  selfiePreview: string | null = null;
  cameraLoading = false;
  cameraError = '';
  selfieCameraOpen = false;
  selfieCaptureMode = false;
  private selfieStream: MediaStream | null = null;

  constructor(
    private moodService: MoodService,
    private postService: PostService,
    private userSession: UserSessionService,
    private userService: UserService,
    private controltaskService: ControltaskService,
    private controlhelpService: ControlhelpService,
    private controlprojectService: ControlprojectService,
    private documentService: DocumentService,
    private controlquestionService: ControlQuestionService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private router: Router
  ) {}

  ngOnInit() {
    this.userSub = this.userSession.user$.subscribe(user => {
      this.handleUserChange(user);
    });
    const current = this.userSession.getUser();
    this.handleUserChange(current);
    this.userSub?.add(
      this.translate.onLangChange.subscribe(() => {
        if (this.currentMonthLabel) {
          this.currentMonthLabel = this.formatMonthLabel(new Date());
        }
      })
    );
  }

  ngOnDestroy() {
    this.userSub?.unsubscribe();
    this.stopSelfieCamera();
  }

  loadTodayMood() {
    if (!this.currentUser?.id) {
      return;
    }

    this.moodService.getTodayMood(this.currentUser.id).subscribe({
      next: (result) => {
        this.selectedMood = result?.mood || null;
        this.moodFeedback = '';
      }
    });
  }

  private handleUserChange(user: any) {
    if (!user) {
      const restored = this.userSession.getUser(true);
      if (!restored) {
        this.currentUser = null;
        return;
      }
      user = restored;
    }

    if (this.lastUserId === user.id && this.currentUser) {
      return;
    }

    this.currentUser = user;
    this.lastUserId = user.id ?? null;
    this.configureModulePermissions(user);
    this.userName = user?.name || user?.email || '';
    this.avatarUrl = this.buildAvatarUrl(user?.picture_user);
    this.selectedMood = null;
    this.loadTodayMood();
    this.loadMoodSummary();
    this.loadPosts();
    this.loadPendingDocuments();
    this.loadPublicDocuments();
    this.loadPendingQuestionnaires();
    this.loadOverdueTask();
    this.loadActiveProjectsCount();
    this.loadActiveProjectsList();
    this.loadMyRequestsCount();
    this.loadMonthBirthdays();
  }

  canViewModule(moduleKey: string) {
    if (!this.hasModuleList) {
      return true;
    }
    return this.allowedModules.has(moduleKey);
  }

  private configureModulePermissions(user: any) {
    const roleName = String(user?.role_name || user?.role?.name || '').toLowerCase();
    if (roleName === 'admin') {
      this.hasModuleList = false;
      this.allowedModules.clear();
      return;
    }

    if (Array.isArray(user?.modules)) {
      this.hasModuleList = true;
      this.allowedModules = new Set(
        user.modules
          .filter((module: any) => !!module?.can_view)
          .map((module: any) => String(module?.module_key || '').trim())
          .filter(Boolean)
      );
      return;
    }

    this.hasModuleList = false;
    this.allowedModules.clear();
  }

  private loadActiveProjectsCount() {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id || !this.canViewModule('controlproject')) {
      this.activeProjectsCount = 0;
      return;
    }

    this.controlprojectService.getActiveProjectsCount(this.currentUser.tenant_id, this.currentUser.id).subscribe({
      next: (result) => {
        this.activeProjectsCount = Number(result?.count || 0);
      },
      error: () => {
        this.activeProjectsCount = 0;
      }
    });
  }

  private loadActiveProjectsList() {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id || !this.canViewModule('controlproject')) {
      this.activeProjectsList = [];
      return;
    }
    this.controlprojectService.getActiveProjectsList(this.currentUser.tenant_id, this.currentUser.id, 12).subscribe({
      next: (rows) => {
        this.activeProjectsList = Array.isArray(rows) ? rows : [];
      },
      error: () => {
        this.activeProjectsList = [];
      }
    });
  }

  private loadMyRequestsCount() {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id || !this.isAdminOrMaster) {
      this.myRequestsOpenCount = 0;
      return;
    }

    this.loadingMyRequests = true;
    this.controlhelpService.getMyRequestsCount(this.currentUser.tenant_id, this.currentUser.id).subscribe({
      next: (result) => {
        this.myRequestsOpenCount = Number(result?.open_count || 0);
        this.loadingMyRequests = false;
      },
      error: () => {
        this.myRequestsOpenCount = 0;
        this.loadingMyRequests = false;
      }
    });
  }

  loadPosts() {
    if (!this.currentUser?.tenant_id) {
      return;
    }

    this.loadingPosts = true;
    this.postsError = '';
    this.latestCommunicationTitle = 'Carregando comunicado...';
    this.expandedPostKeys.clear();
    this.activePopup = null;
    this.popupVisible = false;
    this.postService.getPosts(this.currentUser.tenant_id).subscribe({
      next: (posts) => {
        const mapped = posts.map(post => {
          const mediaUrls = this.extractMediaUrls(post);
          return {
            ...post,
            mediaUrls,
            likesCount: post.likes_count || 0,
            liked: false,
            allow_likes: this.parseFlag(post.allow_likes, true),
            allow_comments: this.parseFlag(post.allow_comments, true),
            show_popup: this.parseFlag(post.show_popup, false),
            popup_expires_at: post.popup_expires_at || null,
            commentsCount: post.comments_count || 0,
            commentsVisible: false,
            comments: [],
            commentsOffset: 0,
            commentsLoading: false,
            commentsEnd: false,
            commentsLoaded: false,
            commentsError: '',
            newComment: '',
            submittingComment: false
          };
        });
        this.latestCommunicationTitle = this.resolveLatestCommunicationTitle(mapped);
        const visiblePosts = mapped.filter(post => !post.show_popup && !this.isProjectCompletionPost(post));
        this.posts = visiblePosts;
        this.visibleCount = Math.min(this.postsPerPage, this.getRegularPosts().length);
        this.updateVisiblePosts();
        this.loadingPosts = false;
        this.visiblePosts.forEach(post => this.loadLikes(post));
        this.evaluatePopup(mapped);
      },
      error: (err) => {
        this.postsError = err?.error?.error || 'Não foi possível carregar os posts.';
        this.latestCommunicationTitle = 'Nenhum comunicado recente';
        this.loadingPosts = false;
        this.visiblePosts = [];
        this.updateVisibleTimelineItems();
        this.popupVisible = false;
        this.activePopup = null;
      }
    });
  }

  private resolveLatestCommunicationTitle(posts: any[]): string {
    if (!Array.isArray(posts) || !posts.length) {
      return 'Nenhum comunicado recente';
    }
    const candidates = posts
      .filter((post) => !this.isProjectCompletionPost(post))
      .filter((post) => String(post?.post_type || '').toLowerCase() !== 'birthday')
      .map((post) => {
        const text = String(post?.content_text || post?.title || '').trim();
        const createdAt = new Date(post?.created_at || post?.posted_at || 0).getTime() || 0;
        return { text, createdAt };
      })
      .filter((item) => !!item.text);
    if (!candidates.length) {
      return 'Nenhum comunicado recente';
    }
    candidates.sort((a, b) => b.createdAt - a.createdAt);
    const text = candidates[0].text.replace(/\s+/g, ' ').trim();
    if (text.length <= 48) {
      return text;
    }
    return `${text.slice(0, 48).trim()}...`;
  }

  private loadMonthBirthdays() {
    if (!this.currentUser?.tenant_id) {
      return;
    }

    this.birthdaysLoading = true;
    this.birthdaysError = '';
    const now = new Date();
    this.currentMonthLabel = this.formatMonthLabel(now);
    const currentMonth = now.getMonth();

    this.userService.getUsersByTenant(this.currentUser.tenant_id).subscribe({
      next: (users) => {
        const list = (users || [])
          .map((user) => {
            const birthDate = this.parseBirthDate(user.birth_date);
            if (!birthDate) {
              return null;
            }
            const isActive = user.active === undefined || user.active === null ? true : Number(user.active) === 1;
            if (!isActive || birthDate.getMonth() !== currentMonth) {
              return null;
            }
            const departmentName =
              user.department_name || user.department?.name || user.department || '';
            const sectorName = user.sector_name || user.sector?.name || '';
            const departmentLabel = departmentName
              ? sectorName
                ? `${sectorName} / ${departmentName}`
                : departmentName
              : sectorName || 'Sem departamento';
            const pictureUrl = this.buildAvatarUrl(
              user.picture_user || user.picture_url || user.avatar || user.photo
            );
            return {
              id: user.id,
              name: user.name || user.email || 'Colaborador',
              birthDate,
              departmentLabel,
              pictureUrl
            };
          })
          .filter((item) => item !== null) as Array<{
          id: number;
          name: string;
          birthDate: Date;
          departmentLabel: string;
          pictureUrl: string | null;
        }>;

        list.sort((a, b) => a.birthDate.getDate() - b.birthDate.getDate());
        this.monthBirthdays = list;
        this.birthdaysLoading = false;
        this.updateVisibleTimelineItems();
      },
      error: (err) => {
        this.birthdaysError = err?.error?.error || 'Não foi possível carregar os aniversariantes.';
        this.birthdaysLoading = false;
        this.monthBirthdays = [];
        this.updateVisibleTimelineItems();
      }
    });
  }

  private parseBirthDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const raw = value.split('T')[0];
    const parts = raw.split('-');
    if (parts.length !== 3) {
      return null;
    }
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  private formatMonthLabel(date: Date): string {
    const locale = this.translate.currentLang === 'en' ? 'en-US' : 'pt-BR';
    const label = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  selectMood(moodId: string) {
    if (!this.currentUser?.id || this.savingMood) {
      return;
    }

    this.savingMood = true;
    this.moodFeedback = '';

    this.moodService.recordMood({
      user_id: this.currentUser.id,
      tenant_id: this.currentUser.tenant_id,
      mood: moodId
    }).subscribe({
      next: () => {
        this.savingMood = false;
        this.selectedMood = moodId;
        this.moodFeedback = '';
        this.loadMoodSummary();
      },
      error: (err) => {
        this.savingMood = false;
        if (err?.status === 409 && err?.error?.mood) {
          this.selectedMood = err.error.mood;
          this.moodFeedback = '';
          this.loadMoodSummary();
        } else {
          this.moodFeedback = err?.error?.error || this.translate.instant('INTRANET.MOOD.FEEDBACK_ERROR');
        }
      }
    });
  }

  private buildAvatarUrl(pathValue?: string | null) {
    if (!pathValue) {
      return null;
    }
    if (pathValue.startsWith('http')) {
      return pathValue;
    }
    return `${this.apiBase}${pathValue}`;
  }

  formatDisplayName(name: string): string {
    if (!name) {
      return name;
    }
    const cleaned = name.replace(/_master$/i, '').trim();
    const parts = cleaned.split(' ').map((part) => part.trim()).filter(Boolean);
    if (parts.length <= 2) {
      return parts.join(' ');
    }
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

  get hasMoodRegistered(): boolean {
    return !!this.selectedMood;
  }

  get isAdminOrMaster(): boolean {
    const roleName = String(this.currentUser?.role_name || this.currentUser?.role?.name || '').toLowerCase();
    return roleName === 'admin' || roleName.includes('master');
  }

  get selectedMoodEmoji(): string {
    if (!this.selectedMood) {
      return '';
    }
    const found = this.moodOptions.find((item) => item.id === this.selectedMood);
    return found?.emoji || '';
  }

  get tenantDisplayName(): string {
    const tenantName =
      this.currentUser?.tenant_name ||
      this.currentUser?.tenant?.name ||
      this.currentUser?.tenant ||
      '';
    return String(tenantName || 'Tenant');
  }

  get attentionDocumentTitle(): string {
    const firstPending = this.pendingDocuments?.[0];
    if (firstPending?.title) {
      return firstPending.title;
    }
    return 'Nenhum documento disponível';
  }

  get attentionQuestionnaireTitle(): string {
    const firstPending = this.pendingQuestionnaires?.[0];
    if (firstPending?.title) {
      return firstPending.title;
    }
    return 'Nenhum questionário pendente';
  }

  get totalPendingCount(): number {
    return Number(this.pendingDocuments.length || 0) + Number(this.pendingQuestionnaires.length || 0);
  }

  get todayTasksCount(): number {
    return Number(this.todayTasks.length || 0);
  }

  get pendingDocumentsCount(): number {
    return Number(this.pendingDocuments.length || 0);
  }

  get publicDocumentsCount(): number {
    return Number(this.publicDocuments.length || 0);
  }

  get visibleMonthBirthdays(): Array<{
    id: number;
    name: string;
    birthDate: Date;
    departmentLabel: string;
    pictureUrl: string | null;
  }> {
    const birthdays = Array.isArray(this.monthBirthdays) ? [...this.monthBirthdays] : [];
    birthdays.sort((a, b) => {
      const aToday = this.isBirthdayToday(a.birthDate) ? 1 : 0;
      const bToday = this.isBirthdayToday(b.birthDate) ? 1 : 0;
      if (aToday !== bToday) {
        return bToday - aToday;
      }
      return a.birthDate.getDate() - b.birthDate.getDate();
    });
    return birthdays;
  }

  formatDocumentTypeLabel(type: unknown): string {
    const normalized = String(type || '').trim().toUpperCase();
    if (normalized === 'POLICY') {
      return 'Política';
    }
    if (normalized === 'TERM') {
      return 'Termo';
    }
    if (normalized === 'DOCUMENT') {
      return 'Documento';
    }
    return String(type || '-').trim() || '-';
  }

  get dayMetricTitle(): string {
    if (this.openDayMetric === 'TASKS') {
      return 'Tarefas de hoje';
    }
    if (this.openDayMetric === 'PENDING') {
      return 'Pendências';
    }
    if (this.openDayMetric === 'PROJECTS') {
      return 'Projetos ativos';
    }
    return '';
  }

  get dayMetricItems(): string[] {
    if (this.openDayMetric === 'TASKS') {
      return (this.todayTasks || []).map((task) => String(task?.title || '').trim()).filter(Boolean);
    }
    if (this.openDayMetric === 'PENDING') {
      return (this.pendingDocuments || []).map((task) => String(task?.title || '').trim()).filter(Boolean);
    }
    if (this.openDayMetric === 'PROJECTS') {
      return (this.activeProjectsList || [])
        .map((project) => {
          const code = String(project?.code || '').trim();
          const name = String(project?.name || '').trim();
          return code ? `${code} · ${name}` : name;
        })
        .filter(Boolean);
    }
    return [];
  }

  get dayMetricEmptyText(): string {
    if (this.openDayMetric === 'TASKS') {
      return 'Nenhuma tarefa para hoje.';
    }
    if (this.openDayMetric === 'PENDING') {
      return 'Nenhuma pendência.';
    }
    if (this.openDayMetric === 'PROJECTS') {
      return 'Nenhum projeto ativo.';
    }
    return '';
  }

  private loadMoodSummary() {
    if (!this.currentUser?.tenant_id || !this.currentUser?.id) {
      this.moodSummaryTotal = 0;
      this.moodSummaryRows = this.moodOptions.map((item) => ({
        id: item.id,
        emoji: item.emoji,
        count: 0,
        percent: 0
      }));
      return;
    }

    this.moodSummaryLoading = true;
    this.moodService.getSummary(this.currentUser.tenant_id, this.currentUser.id, 30).subscribe({
      next: (result) => {
        const sourceRows = Array.isArray(result?.moods) ? result.moods : [];
        const map = new Map(
          sourceRows.map((row: any) => [
            String(row?.mood || ''),
            {
              count: Number(row?.count || 0),
              percent: Number(row?.percent || 0)
            }
          ])
        );

        this.moodSummaryTotal = Number(result?.total_responses || 0);
        this.moodSummaryRows = this.moodOptions.map((item) => {
          const row = map.get(item.id);
          return {
            id: item.id,
            emoji: item.emoji,
            count: Number(row?.count || 0),
            percent: Number(row?.percent || 0)
          };
        });
        this.moodSummaryLoading = false;
      },
      error: () => {
        this.moodSummaryTotal = 0;
        this.moodSummaryRows = this.moodOptions.map((item) => ({
          id: item.id,
          emoji: item.emoji,
          count: 0,
          percent: 0
        }));
        this.moodSummaryLoading = false;
      }
    });
  }

  toggleAdminCard(card: 'quickLinks' | 'certificates' | 'requests') {
    this.adminCardsOpen[card] = !this.adminCardsOpen[card];
  }

  get overdueTaskDelayLabel(): string {
    if (!this.overdueTask) {
      return '';
    }
    const delayDays = this.taskDelayDays(this.overdueTask);
    return `vencida há ${delayDays} dia${delayDays === 1 ? '' : 's'}`;
  }

  get highlightBirthdayPerson(): {
    id: number;
    name: string;
    birthDate: Date;
    departmentLabel: string;
    pictureUrl: string | null;
  } | null {
    const today = this.monthBirthdays.find((person) => this.isBirthdayToday(person.birthDate));
    return today || null;
  }

  formatBirthdayDate(date: Date): string {
    if (!date) {
      return '';
    }
    const day = date.getDate();
    if (this.isBirthdayToday(date)) {
      return `${day}° HOJE`;
    }
    const monthLabel = (this.currentMonthLabel || this.formatMonthLabel(date)).toUpperCase();
    return `${day}° ${monthLabel}`;
  }

  private isBirthdayToday(date: Date): boolean {
    const reference = this.getSaoPauloDayMonth(new Date());
    if (!reference) {
      const now = new Date();
      return date.getDate() === now.getDate() && date.getMonth() === now.getMonth();
    }
    return date.getDate() === reference.day && date.getMonth() === reference.month;
  }

  private getSaoPauloDayMonth(value: Date): { day: number; month: number } | null {
    const formatter = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit'
    });
    const parts = formatter.formatToParts(value);
    const day = Number(parts.find((part) => part.type === 'day')?.value || 0);
    const month = Number(parts.find((part) => part.type === 'month')?.value || 0);
    if (!day || !month) {
      return null;
    }
    return { day, month: month - 1 };
  }

  private loadOverdueTask() {
    if (!this.currentUser?.tenant_id) {
      this.overdueTask = null;
      return;
    }

    this.loadingOverdueTask = true;
    const filters: Record<string, any> = {};
    if (this.currentUser?.id) {
      filters['user_id'] = this.currentUser.id;
    }
    const departmentId = Number(this.currentUser?.department_id || this.currentUser?.department?.id || 0);
    if (departmentId) {
      filters['department_id'] = departmentId;
    }

    this.controltaskService.getTasks(this.currentUser.tenant_id, filters).subscribe({
      next: (tasks) => {
        const taskRows = tasks || [];
        this.todayTasks = taskRows.filter((task) => this.isTaskForToday(task) && !this.isTaskFinished(task.status));
        const overdueTasks = (tasks || [])
          .filter((task) => this.isOverdueTask(task))
          .sort((a, b) => this.taskDelayDays(a) - this.taskDelayDays(b));
        this.overdueTask = overdueTasks[0] || null;
        this.loadingOverdueTask = false;
      },
      error: () => {
        this.todayTasks = [];
        this.overdueTask = null;
        this.loadingOverdueTask = false;
      }
    });
  }

  private isTaskForToday(task: ControlTask): boolean {
    if (!task?.due_date) {
      return false;
    }
    const dueKey = this.dateKey(task.due_date, 'America/Sao_Paulo');
    const todayKey = this.dateKey(new Date(), 'America/Sao_Paulo');
    if (!dueKey || !todayKey) {
      return false;
    }
    return dueKey === todayKey;
  }

  private isOverdueTask(task: ControlTask): boolean {
    if (!task?.due_date || this.isTaskFinished(task.status)) {
      return false;
    }
    const dueKey = this.dateKey(task.due_date, 'America/Sao_Paulo');
    const todayKey = this.dateKey(new Date(), 'America/Sao_Paulo');
    if (!dueKey || !todayKey) {
      return false;
    }
    return dueKey < todayKey;
  }

  private isTaskFinished(status?: string | null): boolean {
    const normalized = String(status || '').toUpperCase();
    return normalized === 'COMPLETED';
  }

  private taskDelayDays(task: ControlTask): number {
    if (!task?.due_date) {
      return 0;
    }
    const dueKey = this.dateKey(task.due_date, 'America/Sao_Paulo');
    const todayKey = this.dateKey(new Date(), 'America/Sao_Paulo');
    if (!dueKey || !todayKey) {
      return 0;
    }
    const dueDate = new Date(`${dueKey}T00:00:00`);
    const todayDate = new Date(`${todayKey}T00:00:00`);
    const diffDays = Math.floor((todayDate.getTime() - dueDate.getTime()) / 86400000);
    return Math.max(0, diffDays);
  }

  private dateKey(value?: unknown, timeZone = 'America/Sao_Paulo'): string | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      return this.toDateKey(value, timeZone);
    }
    const normalized = String(value);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return this.toDateKey(parsed, timeZone);
  }

  private toDateKey(date: Date, timeZone: string): string {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(date);
  }

  onAvatarSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file || !this.currentUser?.id || this.uploadingAvatar) {
      if (input) {
        input.value = '';
      }
      return;
    }

    this.uploadingAvatar = true;
    this.userService.uploadAvatar(this.currentUser.id, file).subscribe({
      next: (response) => {
        const picturePath = response?.picture_url || response?.picture_user || null;
        this.avatarUrl = this.buildAvatarUrl(picturePath);
        if (picturePath) {
          this.userSession.patchUser({ picture_user: picturePath });
        }
        this.uploadingAvatar = false;
        if (input) {
          input.value = '';
        }
      },
      error: () => {
        this.uploadingAvatar = false;
        if (input) {
          input.value = '';
        }
      }
    });
  }

  formatFileUrl(pathValue?: string | null) {
    if (!pathValue) {
      return null;
    }
    if (pathValue.startsWith('http')) {
      return pathValue;
    }
    return `${this.apiBase}${pathValue}`;
  }

  private buildDocumentViewerUrl(pathValue?: string | null) {
    if (!pathValue) {
      return null;
    }

    const rawValue = String(pathValue).trim();
    if (!rawValue) {
      return null;
    }

    const isUploadPath = rawValue.startsWith('/uploads/');
    if (isUploadPath) {
      return `${this.apiBase}/documents/view-file?path=${encodeURIComponent(rawValue)}`;
    }

    if (/^https?:\/\//i.test(rawValue)) {
      return rawValue;
    }

    return this.formatFileUrl(rawValue);
  }

  private loadPendingDocuments() {
    if (!this.currentUser?.id || !this.currentUser?.tenant_id) {
      this.pendingDocuments = [];
      return;
    }
    this.loadingPendingDocs = true;
    this.documentService.getPendingDocuments(this.currentUser.id, this.currentUser.tenant_id).subscribe({
      next: (docs) => {
        this.pendingDocuments = docs;
        this.loadingPendingDocs = false;
      },
      error: () => {
        this.pendingDocuments = [];
        this.loadingPendingDocs = false;
      }
    });
  }

  private loadPublicDocuments() {
    if (!this.currentUser?.tenant_id) {
      this.publicDocuments = [];
      return;
    }
    this.loadingPublicDocs = true;
    this.documentService.getPublicDocuments(this.currentUser.tenant_id).subscribe({
      next: (docs) => {
        this.publicDocuments = Array.isArray(docs) ? docs : [];
        this.loadingPublicDocs = false;
      },
      error: () => {
        this.publicDocuments = [];
        this.loadingPublicDocs = false;
      }
    });
  }

  private loadPendingQuestionnaires() {
    if (!this.currentUser?.id || !this.currentUser?.tenant_id) {
      this.pendingQuestionnaires = [];
      return;
    }
    this.loadingPendingQuestionnaires = true;
    this.controlquestionService
      .listRecipientSurveys(this.currentUser.tenant_id, this.currentUser.id, 'PENDING')
      .subscribe({
        next: (rows) => {
          this.pendingQuestionnaires = Array.isArray(rows) ? rows : [];
          this.loadingPendingQuestionnaires = false;
        },
        error: () => {
          this.pendingQuestionnaires = [];
          this.loadingPendingQuestionnaires = false;
        }
      });
  }

  isVideo(post: any) {
    if (String(post?.post_type).toLowerCase() === 'video') {
      return true;
    }
    const target = post?.mediaUrls?.[0] || post?.file_url;
    if (!target) {
      return false;
    }
    return this.isVideoPath(target);
  }

  isCarousel(post: any) {
    return String(post?.post_type).toLowerCase() === 'carousel' || (post?.mediaUrls?.length || 0) > 1;
  }

  private isVideoPath(path?: string | null) {
    if (!path) return false;
    return /\.(mp4|webm|ogg|mov)$/i.test(path);
  }

  toggleLike(post: any) {
    if (!post.allow_likes || !this.currentUser?.id || !this.currentUser?.tenant_id) {
      return;
    }

    if (post.liked) {
      this.postService.unlikePost(post.id, this.currentUser.id).subscribe({
        next: () => {
          post.liked = false;
          post.likesCount = Math.max(0, (post.likesCount || 1) - 1);
        },
        error: () => {}
      });
    } else {
      this.postService.likePost(post.id, this.currentUser.id, this.currentUser.tenant_id).subscribe({
        next: () => {
          post.liked = true;
          post.likesCount = (post.likesCount || 0) + 1;
        },
        error: () => {}
      });
    }
  }

  private loadLikes(post: any) {
    if (!this.currentUser?.id) {
      return;
    }

    if (String(post?.post_type || '').toLowerCase() === 'birthday') {
      const birthdayUserId = Number(post?.birthday_user_id || post?.user_id || 0);
      const birthdayDate = this.dateKey(post?.birthday_date || post?.posted_at || post?.created_at);
      if (!birthdayUserId || !birthdayDate || !this.currentUser?.tenant_id) {
        return;
      }
      this.postService.getBirthdayLikeStatus(
        this.currentUser.tenant_id,
        birthdayUserId,
        this.currentUser.id,
        birthdayDate
      ).subscribe({
        next: (res) => {
          post.likesCount = res.count;
          post.liked = res.liked;
          this.updateVisibleTimelineItems();
        },
        error: () => {}
      });
      return;
    }

    this.postService.getPostLikes(post.id, this.currentUser.id).subscribe({
      next: (res) => {
        post.likesCount = res.count;
        post.liked = res.liked;
      },
      error: () => {}
    });
  }

  toggleComments(post: any) {
    if (!post.allow_comments) {
      return;
    }
    post.commentsVisible = !post.commentsVisible;
    if (post.commentsVisible && !post.commentsLoaded) {
      this.loadComments(post, true);
    }
  }

  loadComments(post: any, reset = false) {
    if (post.commentsLoading) {
      return;
    }

    post.commentsLoading = true;
    post.commentsError = '';

    const offset = reset ? 0 : post.commentsOffset || 0;
    this.postService.getComments(post.id, offset, this.commentsPageSize).subscribe({
      next: (comments) => {
        if (reset) {
          post.comments = comments;
        } else {
          post.comments = [...(post.comments || []), ...comments];
        }
        post.commentsOffset = (post.comments?.length) || 0;
        post.commentsLoaded = true;
        post.commentsEnd = comments.length < this.commentsPageSize;
        post.commentsLoading = false;
      },
      error: (err) => {
        post.commentsError = err?.error?.error || 'Não foi possível carregar os comentários.';
        post.commentsLoading = false;
      }
    });
  }

  loadMoreComments(post: any) {
    this.loadComments(post, false);
  }

  closeComments(post: any) {
    post.commentsVisible = false;
  }

  submitComment(post: any) {
    if (!this.currentUser?.id || !this.currentUser?.tenant_id || !post.newComment?.trim() || post.submittingComment) {
      return;
    }

    post.submittingComment = true;

    this.postService.addComment(post.id, {
      user_id: this.currentUser.id,
      tenant_id: this.currentUser.tenant_id,
      comment_text: post.newComment.trim()
    }).subscribe({
      next: (comment) => {
        post.newComment = '';
        post.submittingComment = false;
        post.comments = [comment, ...(post.comments || [])];
        post.commentsOffset = (post.comments?.length) || 0;
        post.commentsCount = (post.commentsCount || 0) + 1;
      },
      error: (err) => {
        post.submittingComment = false;
        post.commentsError = err?.error?.error || 'Não foi possível enviar o comentário.';
      }
    });
  }

  addEmoji(post: any, emoji: string) {
    post.newComment = `${post.newComment || ''}${emoji}`;
  }

  private parseFlag(value: any, fallback = false) {
    if (value === undefined || value === null) {
      return fallback;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    const normalized = Number(value);
    if (!Number.isNaN(normalized)) {
      return normalized === 1;
    }
    return Boolean(value);
  }

  private extractMediaUrls(post: any): string[] {
    if (Array.isArray(post?.media_urls) && post.media_urls.length) {
      return post.media_urls;
    }
    return this.parseMediaField(post?.file_url);
  }

  private parseMediaField(value: any): string[] {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    }
    return [value];
  }

  private evaluatePopup(sourcePosts?: any[]) {
    const collection = sourcePosts || this.posts;
    const popupCandidate = collection.find(post => this.isPopupEligible(post));
    this.activePopup = popupCandidate || null;
    this.popupVisible = !!popupCandidate;
  }

  private isPopupEligible(post: any) {
    if (!post || post.show_popup !== true || String(post.status).toLowerCase() !== 'published') {
      return false;
    }
    if (!post?.mediaUrls?.length) {
      return false;
    }
    if (post.popup_expires_at) {
      const expires = new Date(post.popup_expires_at);
      if (expires.getTime() <= Date.now()) {
        return false;
      }
    }
    return true;
  }

  closePopup() {
    this.popupVisible = false;
    this.activePopup = null;
  }

  showLoadMoreButton() {
    return this.visibleCount < this.getRegularPosts().length;
  }

  loadMorePosts() {
    if (!this.showLoadMoreButton()) {
      return;
    }
    this.visibleCount = Math.min(this.getRegularPosts().length, this.visibleCount + this.postsPerPage);
    this.updateVisiblePosts();
    this.visiblePosts.forEach(post => this.loadLikes(post));
  }

  private updateVisiblePosts() {
    const birthdays = this.posts.filter((post) => this.isBirthdayPost(post));
    const regularPosts = this.getRegularPosts().slice(0, this.visibleCount);
    this.visiblePosts = [...birthdays, ...regularPosts];
    this.updateVisibleTimelineItems();
  }

  private getRegularPosts() {
    return this.posts.filter((post) => !this.isBirthdayPost(post));
  }

  private isBirthdayPost(post: any): boolean {
    return String(post?.post_type || '').toLowerCase() === 'birthday';
  }

  private updateVisibleTimelineItems() {
    const merged = (this.visiblePosts || []).map((post) => {
      const isBirthday = String(post?.post_type || '').toLowerCase() === 'birthday';
      const avatarUrl = this.buildAvatarUrl(post?.author_picture || post?.picture_user || null);
      if (isBirthday) {
        return {
          kind: 'birthday',
          key: `birthday-post-${post.id}`,
          postedAt: post.posted_at || post.created_at || new Date().toISOString(),
          post,
          birthday: {
            id: Number(post?.birthday_user_id || post?.user_id || 0),
            name: post?.author_name || 'Colaborador',
            birthDate: this.parseBirthDate(post?.birthday_date) || new Date(post?.birthday_date || post?.posted_at || Date.now()),
            departmentLabel: '',
            pictureUrl: avatarUrl
          },
          authorName: post.author_name || 'Usuário',
          avatarUrl,
          liked: !!post.liked,
          likesCount: Number(post.likesCount || 0)
        };
      }

      return {
        kind: 'post',
        key: `post-${post.id}`,
        postedAt: post.posted_at || post.created_at || new Date().toISOString(),
        post,
        authorName: post.author_name || 'Usuário',
        avatarUrl
      };
    });
    merged.sort((a, b) => {
      const left = new Date(a.postedAt).getTime();
      const right = new Date(b.postedAt).getTime();
      return right - left;
    });
    this.visibleTimelineItems = merged;
  }

  timelineAuthor(item: any): string {
    if (!item) {
      return 'Usuário';
    }
    return item.authorName || item.post?.author_name || 'Usuário';
  }

  timelineAvatarUrl(item: any): string | null {
    return item?.avatarUrl || null;
  }

  birthdayTimelineTitle(item: any): string {
    if (this.isBirthdayTimelineToday(item)) {
      return '🎉 Faz aniversário hoje!';
    }
    return `🎉 Aniversário de ${this.timelineAuthor(item)}`;
  }

  birthdayTimelineSubtitle(item: any): string {
    if (this.isBirthdayTimelineToday(item)) {
      return `Parabéns, ${this.timelineAuthor(item)}! Muito sucesso!`;
    }
    return `Parabéns para ${this.timelineAuthor(item)}!`;
  }

  private isBirthdayTimelineToday(item: any): boolean {
    const dateKey = this.dateKey(item?.post?.birthday_date || item?.postedAt || item?.post?.posted_at || item?.post?.created_at);
    const todayKey = this.dateKey(new Date(), 'America/Sao_Paulo');
    return !!dateKey && !!todayKey && dateKey === todayKey;
  }

  timelineTimeLabel(value: string | Date | null | undefined): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) {
      return this.formatTodayTime(date);
    }
    if (diffMs < 24 * 60 * 60 * 1000) {
      const sameDay =
        this.dateKey(now, 'America/Sao_Paulo') === this.dateKey(date, 'America/Sao_Paulo');
      if (sameDay) {
        return this.formatTodayTime(date);
      }
      const hours = Math.max(1, Math.floor(diffMs / (60 * 60 * 1000)));
      return `${hours} hora${hours === 1 ? '' : 's'} atrás`;
    }
    const days = Math.max(1, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
    return `${days} dia${days === 1 ? '' : 's'} atrás`;
  }

  isPostExpandable(post: any): boolean {
    const text = String(post?.content_text || '');
    if (!text.trim()) {
      return false;
    }
    const lineBreaks = (text.match(/\n/g) || []).length;
    return text.length > this.postPreviewCharLimit || lineBreaks >= 3;
  }

  isPostExpanded(post: any): boolean {
    return this.expandedPostKeys.has(this.getPostExpansionKey(post));
  }

  togglePostExpanded(post: any) {
    const key = this.getPostExpansionKey(post);
    if (!key) {
      return;
    }
    if (this.expandedPostKeys.has(key)) {
      this.expandedPostKeys.delete(key);
      return;
    }
    this.expandedPostKeys.add(key);
  }

  private getPostExpansionKey(post: any): string {
    if (post?.id != null) {
      return `post-${post.id}`;
    }
    return `post-fallback-${post?.created_at || post?.posted_at || ''}`;
  }

  toggleBirthdayLike(item: any) {
    const birthdayUserId = Number(item?.birthday?.id || item?.post?.birthday_user_id || item?.post?.user_id || 0);
    const birthdayDate = this.dateKey(item?.post?.birthday_date || item?.postedAt || item?.post?.posted_at || item?.post?.created_at);
    if (!birthdayUserId || !birthdayDate || !this.currentUser?.tenant_id || !this.currentUser?.id) {
      return;
    }
    const targetPost = item?.post || null;
    const current = {
      liked: !!(targetPost ? targetPost.liked : item.liked),
      count: Number(targetPost ? targetPost.likesCount || 0 : item.likesCount || 0)
    };
    const previous = { liked: current.liked, count: current.count };
    const shouldLike = !current.liked;
    const optimisticCount = shouldLike ? previous.count + 1 : Math.max(0, previous.count - 1);
    if (targetPost) {
      targetPost.liked = shouldLike;
      targetPost.likesCount = optimisticCount;
    }
    item.liked = shouldLike;
    item.likesCount = optimisticCount;
    this.updateVisibleTimelineItems();

    const request$ = shouldLike
      ? this.postService.likeBirthdayPost(this.currentUser.tenant_id, birthdayUserId, this.currentUser.id, birthdayDate)
      : this.postService.unlikeBirthdayPost(this.currentUser.tenant_id, birthdayUserId, this.currentUser.id, birthdayDate);

    request$.subscribe({
      next: (state) => {
        if (targetPost) {
          targetPost.liked = !!state?.liked;
          targetPost.likesCount = Number(state?.count || 0);
        }
        item.liked = !!state?.liked;
        item.likesCount = Number(state?.count || 0);
        this.updateVisibleTimelineItems();
      },
      error: () => {
        if (targetPost) {
          targetPost.liked = previous.liked;
          targetPost.likesCount = previous.count;
        }
        item.liked = previous.liked;
        item.likesCount = previous.count;
        this.updateVisibleTimelineItems();
      }
    });
  }

  private formatTodayTime(date: Date): string {
    const time = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
    return `Hoje, ${time}`;
  }

  private isProjectCompletionPost(post: any): boolean {
    const type = String(post?.post_type || '').toLowerCase();
    const text = String(post?.content_text || '').toLowerCase();
    const isProjectType =
      type.includes('project') && (type.includes('complete') || type.includes('done') || type.includes('conclu'));
    const hasProjectCompletedText =
      text.includes('projeto concluído') || text.includes('projeto concluido') || text.includes('100% completo');
    return isProjectType || hasProjectCompletedText;
  }

  openPendingList() {
    this.loadPendingDocuments();
    this.loadPublicDocuments();
    this.pendingModalTab = 'public';
    this.pendingModalOpen = true;
  }

  setPendingModalTab(tab: 'pending' | 'public') {
    this.pendingModalTab = tab;
  }

  toggleDayMetric(metric: 'TASKS' | 'PENDING' | 'PROJECTS') {
    this.openDayMetric = this.openDayMetric === metric ? null : metric;
  }

  closeDayMetricTooltip() {
    this.openDayMetric = null;
  }

  closePendingModal() {
    this.pendingModalOpen = false;
    this.closeDocumentModal();
  }

  openDocumentTask(task: any) {
    this.pendingModalOpen = false;
    this.selectedTaskMode = 'pending';
    this.selectedTask = task;
    this.documentActionsChecklist = this.buildActionChecklist(task);
    this.documentScrollReady = false;
    this.documentModalOpen = true;
    this.documentFeedback = '';
    this.locationEvidence = null;
    this.locationLoading = false;
    this.locationError = '';
    this.selfieFile = null;
    this.selfiePreview = null;
    this.selfieCaptureMode = false;
    const url = this.buildDocumentViewerUrl(task.file_path);
    const viewerUrl = url ? this.withViewerParams(url) : null;
    this.safeDocumentUrl = viewerUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl) : null;
    if (!url) {
      this.documentScrollReady = true;
    }
  }

  openPublicDocumentTask(task: any) {
    this.pendingModalOpen = false;
    this.selectedTaskMode = 'public';
    this.selectedTask = {
      ...task,
      title: task?.title || task?.content_text || `Documento #${task?.id || '—'}`
    };
    this.documentActionsChecklist = [];
    this.documentScrollReady = true;
    this.documentModalOpen = true;
    this.documentFeedback = '';
    this.locationEvidence = null;
    this.locationLoading = false;
    this.locationError = '';
    this.selfieFile = null;
    this.selfiePreview = null;
    this.selfieCaptureMode = false;
    const url = this.buildDocumentViewerUrl(task.file_url);
    const normalizedUrl = url || this.buildDocumentViewerUrl(task.file_path);
    const viewerUrl = normalizedUrl ? this.withViewerParams(normalizedUrl) : null;
    this.safeDocumentUrl = viewerUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl) : null;
  }

  openQuestionnaireInbox() {
    this.pendingModalOpen = false;
    this.router.navigate(['/controlquestion-intranet']);
  }

  openQuestionnaireTask(task: any) {
    if (!task?.link_token) {
      this.openQuestionnaireInbox();
      return;
    }
    this.pendingModalOpen = false;
    this.router.navigate(['/controlquestion-intranet/respond'], {
      state: {
        token: task.link_token,
        respondent_user_id: this.currentUser?.id,
        recipient_id: task.recipient_id,
        autostart: 1
      }
    });
  }

  closeDocumentModal() {
    this.documentModalOpen = false;
    this.selectedTask = null;
    this.selectedTaskMode = 'pending';
    this.safeDocumentUrl = null;
    this.documentFeedback = '';
    this.locationEvidence = null;
    this.locationLoading = false;
    this.locationError = '';
    this.selfieFile = null;
    this.selfiePreview = null;
    this.cameraError = '';
    this.selfieCaptureMode = false;
    this.stopSelfieCamera();
  }

  private buildActionChecklist(task: any): DocumentActionItem[] {
    const checklist: DocumentActionItem[] = [];
    if (this.parseFlag(task.require_read, true)) {
      checklist.push({ type: 'READ', label: 'Confirmo que li o documento.', completed: false });
    }
    if (this.parseFlag(task.require_accept, false)) {
      checklist.push({ type: 'ACCEPT', label: 'Aceito os termos deste documento.', completed: false });
    }
    if (this.parseFlag(task.require_agreement, false)) {
      checklist.push({ type: 'AGREE', label: 'Estou de acordo com este conteúdo.', completed: false });
    }
    if (this.parseFlag(task.require_signature, false)) {
      checklist.push({ type: 'SIGN', label: 'Assinar digitalmente este documento.', completed: false });
    }
    return checklist;
  }

  onDocumentScroll(event: Event) {
    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 16;
    if (reachedBottom) {
      this.documentScrollReady = true;
    }
  }

  toggleChecklistItem(item: DocumentActionItem) {
    if (!this.documentScrollReady) {
      return;
    }
    item.completed = !item.completed;
  }

  canSubmitDocumentAction() {
    if (!this.documentScrollReady) {
      return false;
    }
    if (!this.documentActionsChecklist.length) {
      if (this.requiresGeolocation() && !this.locationEvidence) {
        return false;
      }
      if (this.requiresSelfie() && !this.selfieFile) {
        return false;
      }
      return true;
    }
    const checklistReady = this.documentActionsChecklist.every(item => item.completed);
    if (!checklistReady) {
      return false;
    }
    if (this.requiresGeolocation() && !this.locationEvidence) {
      return false;
    }
    if (this.requiresSelfie() && !this.selfieFile) {
      return false;
    }
    return true;
  }

  requiresSelfie() {
    return this.parseFlag(this.selectedTask?.require_selfie, false);
  }

  requiresGeolocation() {
    return this.parseFlag(this.selectedTask?.require_geolocation, false);
  }

  async captureGeolocation(silent = false): Promise<boolean> {
    if (!silent) {
      this.locationError = '';
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      if (!silent) {
        this.locationError = 'Geolocalização não suportada neste dispositivo.';
      }
      return false;
    }
    this.locationLoading = true;
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.locationLoading = false;
          this.locationEvidence = {
            latitude: Number(position.coords.latitude),
            longitude: Number(position.coords.longitude),
            accuracy: position.coords.accuracy || null,
            provider: 'browser',
            source: 'navigator.geolocation'
          };
          if (!silent) {
            this.locationError = '';
          }
          resolve(true);
        },
        () => {
          this.locationLoading = false;
          if (!silent) {
            this.locationError = 'Não foi possível capturar a geolocalização. Verifique a permissão do navegador.';
          }
          resolve(false);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }

  onSelfieSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (file) {
      this.applySelfieFile(file);
    }
    input.value = '';
  }

  async beginSelfieSignature() {
    if (!this.locationEvidence) {
      // Quando houver biometria, tentamos capturar geolocalização por padrão para reforçar a prova digital.
      await this.captureGeolocation(true);
    }
    this.selfieCaptureMode = true;
    this.selfieFile = null;
    this.selfiePreview = null;
    await this.openSelfieCamera();
    if (!this.selfieCameraOpen) {
      this.selfieCaptureMode = false;
    }
  }

  cancelSelfieSignature() {
    this.selfieCaptureMode = false;
    this.stopSelfieCamera();
  }

  async openSelfieCamera() {
    this.cameraError = '';
    this.documentFeedback = '';
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.cameraError = 'Câmera não suportada neste dispositivo. Use o envio de arquivo.';
      return;
    }

    this.cameraLoading = true;
    this.stopSelfieCamera();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      });
      this.selfieStream = stream;
      this.selfieCameraOpen = true;
      this.cameraLoading = false;
      setTimeout(() => this.attachSelfieStream(), 0);
    } catch (_error) {
      this.cameraLoading = false;
      this.cameraError = 'Não foi possível acessar a câmera. Verifique a permissão do navegador.';
    }
  }

  closeSelfieCamera() {
    this.stopSelfieCamera();
  }

  captureSelfieFromCamera() {
    this.cameraError = '';
    const video = this.selfieVideoRef?.nativeElement;
    const canvas = this.selfieCanvasRef?.nativeElement;
    if (!video || !canvas || !this.selfieStream) {
      this.cameraError = 'A câmera não está pronta para captura.';
      return;
    }
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.cameraError = 'A câmera ainda está iniciando. Aguarde 1 segundo e tente novamente.';
      return;
    }

    const width = video.videoWidth || Math.max(720, Math.floor(video.clientWidth || 0));
    const height = video.videoHeight || Math.max(540, Math.floor(video.clientHeight || 0));
    if (width < 2 || height < 2) {
      this.cameraError = 'Não foi possível ler a imagem da câmera. Tente novamente.';
      return;
    }
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.cameraError = 'Não foi possível capturar a imagem da câmera.';
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
        this.applySelfieFile(file);
        this.selfieCaptureMode = false;
        this.stopSelfieCamera();
        return;
      }
      try {
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        const fallback = this.dataUrlToFile(dataUrl, `selfie-${Date.now()}.jpg`);
        this.applySelfieFile(fallback);
        this.selfieCaptureMode = false;
        this.stopSelfieCamera();
      } catch (_error) {
        this.cameraError = 'Falha ao gerar a selfie. Tente novamente.';
      }
    }, 'image/jpeg', 0.92);
  }

  private applySelfieFile(file: File) {
    if (!file.type.startsWith('image/')) {
      this.documentFeedback = 'Envie uma imagem válida para a selfie.';
      return;
    }
    this.selfieFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      this.selfiePreview = typeof reader.result === 'string' ? reader.result : null;
    };
    reader.readAsDataURL(file);
  }

  private attachSelfieStream() {
    const video = this.selfieVideoRef?.nativeElement;
    if (!video || !this.selfieStream) {
      return;
    }
    video.srcObject = this.selfieStream;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.onloadedmetadata = () => {
      video.play().catch(() => {});
    };
  }

  private dataUrlToFile(dataUrl: string, filename: string): File {
    const [meta, base64] = dataUrl.split(',');
    const mimeMatch = meta.match(/data:(.*?);base64/);
    const mime = mimeMatch?.[1] || 'image/jpeg';
    const binary = atob(base64 || '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], filename, { type: mime });
  }

  private stopSelfieCamera() {
    if (this.selfieStream) {
      this.selfieStream.getTracks().forEach((track) => track.stop());
      this.selfieStream = null;
    }
    const video = this.selfieVideoRef?.nativeElement;
    if (video) {
      video.pause();
      video.srcObject = null;
    }
    this.selfieCameraOpen = false;
    this.cameraLoading = false;
  }

  removeSelfie() {
    this.selfieFile = null;
    this.selfiePreview = null;
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Falha ao ler arquivo.'));
      reader.readAsDataURL(file);
    });
  }

  async completeDocumentAction() {
    if (this.selectedTaskMode !== 'pending') {
      return;
    }
    if (!this.selectedTask || !this.canSubmitDocumentAction()) {
      return;
    }
    if (!this.locationEvidence && this.requiresSelfie()) {
      await this.captureGeolocation(true);
    }
    if (this.requiresGeolocation() && !this.locationEvidence) {
      this.documentFeedback = 'Este documento exige geolocalização. Capture a localização antes de concluir.';
      return;
    }
    if (this.requiresSelfie() && !this.selfieFile) {
      this.documentFeedback = 'Este documento exige selfie. Envie uma selfie antes de concluir.';
      return;
    }
    const actions = this.documentActionsChecklist.length
      ? this.documentActionsChecklist.map(item => item.type)
      : ['READ'];
    const payload: any = { actions };
    if (this.locationEvidence) {
      payload.geo = this.locationEvidence;
    }
    if (this.selfieFile) {
      try {
        payload.selfie_data_url = await this.fileToDataUrl(this.selfieFile);
      } catch (_error) {
        this.documentFeedback = 'Não foi possível processar a selfie. Tente novamente.';
        return;
      }
    }
    this.documentFeedback = '';
    this.documentService.completeRecipient(this.selectedTask.recipient_id, payload).subscribe({
      next: () => {
        this.pendingDocuments = this.pendingDocuments.filter(doc => doc.recipient_id !== this.selectedTask?.recipient_id);
        this.closeDocumentModal();
        if (!this.pendingDocuments.length) {
          this.closePendingModal();
        }
      },
      error: (err) => {
        this.documentFeedback = err?.error?.error || 'Não foi possível concluir este documento.';
      }
    });
  }

  private withViewerParams(url: string) {
    const hasHash = url.includes('#');
    const joinChar = hasHash ? '&' : '#';
    return `${url}${joinChar}toolbar=0&navpanes=0&scrollbar=0`;
  }
}
